import React from 'react';
import axiosInstance from 'lib/backend-provider';
import { isAxiosError } from 'axios';
import { setCookie } from 'nookies';

// Types for auth operations
export interface RegisterFormData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  preferences: string;
  isSubscribed: boolean;
}

export interface ForgotPasswordFormData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  password: string;
  password_confirmation: string;
}

export interface VerifyData {
  email: string;
  code: string;
}

export const useAuth = () => {
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setEmail: React.Dispatch<React.SetStateAction<string>>,
    setIsEmailValid: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
  };

  const handleSignUp = async (
    data: RegisterFormData,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ): Promise<boolean> => {
    try {
      const response = await axiosInstance.post('/register', data);

      // Store email in cookie for verification page
      setCookie(null, 'email', response.data.user.email, {
        maxAge: 60 * 3,
        path: '/',
      });

      // Notify Discord of new registration (fire-and-forget)
      fetch('/api/notifications/user-registered', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: response.data.user.first_name,
          last_name: response.data.user.last_name,
          email: response.data.user.email,
        }),
      }).catch(() => {});

      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        const errorMessage =
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : 'Registration failed. Please try again.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      return false;
    }
  };

  const handleVerify = async (
    verificationCode: string[],
    email: string,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ): Promise<boolean> => {
    const code = verificationCode.join('');

    try {
      await axiosInstance.post(
        '/validate',
        { email, code },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        const errorMessage =
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : 'Verification failed. Please try again.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      return false;
    }
  };

  const handleResend = () => {
    alert('on resend');
  };

  const handleVerifyInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    verificationCode: string[],
    setVerificationCode: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const { value } = e.target;
    if (value.length > 1) return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    if (value !== '' && index < 5) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleForgotPassword = async (
    data: ForgotPasswordFormData,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ): Promise<boolean> => {
    try {
      await axiosInstance.post('/verify-reset-password', data, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        const errorMessage =
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : 'Failed to send reset instructions. Please try again.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      return false;
    }
  };

  const handleResetPassword = async (
    data: ResetPasswordData,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ): Promise<boolean> => {
    try {
      await axiosInstance.post('/validate-reset-password', data, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      return true;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        const errorMessage =
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : 'Failed to reset password. Please try again.';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      return false;
    }
  };

  return {
    validateEmail,
    handleEmailChange,
    handleSignUp,
    handleVerify,
    handleResend,
    handleVerifyInputChange,
    handleForgotPassword,
    handleResetPassword,
  };
};
